import { lazy, createElement, ComponentType } from "react";

const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';
let memoryReloadFlag = false;
let reloadScheduled = false;

/**
 * True once a chunk-load recovery reload has been scheduled in this page session.
 * Used by ErrorBoundary to show a neutral loading state (instead of the error UI)
 * for the brief frame before the reload takes effect.
 */
export function isReloadScheduled(): boolean {
  return reloadScheduled;
}

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message || '';
  const name = error.name || '';
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('Importing a module script failed')
  );
}

function hasAlreadyReloaded(): boolean {
  if (memoryReloadFlag) return true;
  try {
    const val = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!val) return false;
    const ts = parseInt(val, 10);
    if (Date.now() - ts < 30_000) return true;
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    return false;
  } catch {
    return memoryReloadFlag;
  }
}

function markReloadAttempted(): void {
  memoryReloadFlag = true;
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {}
}

function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(() =>
    importFn().catch((error: unknown) => {
      if (isChunkLoadError(error) && !hasAlreadyReloaded()) {
        markReloadAttempted();
        reloadScheduled = true;
        window.location.reload();
      }
      throw error;
    })
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createLazyImport = <T extends ComponentType<any> = ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  fallbackComponent?: ComponentType
) => {
  const LazyComponent = lazyWithRetry(importFn);
  
  return LazyComponent;
};

/**
 * A lazy import that can be resolved BEFORE React's first render.
 *
 * React.lazy always suspends on its first render — even when the module is
 * already in the cache, the thenable resolves in a microtask, so the Suspense
 * fallback can paint for a frame. On prerendered public pages that frame is
 * visible: the copy the server sent is thrown away, "Loading page..." paints,
 * and the copy comes back.
 *
 * `preload()` stores the resolved component in module scope; once it has run,
 * the returned component renders it synchronously and never suspends. Until
 * then it behaves exactly like the plain lazy import (including the
 * chunk-error reload in lazyWithRetry).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createPreloadableLazyImport = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) => {
  let Loaded: T | null = null;
  const LazyComponent = lazyWithRetry(importFn);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function Preloadable(props: any) {
    return Loaded
      ? createElement(Loaded, props)
      : createElement(LazyComponent, props);
  }

  Preloadable.preload = (): Promise<void> =>
    importFn().then(
      (m) => {
        Loaded = m.default;
      },
      () => {
        // Swallow: the lazy path retries the import on render and handles
        // chunk errors (including the one-shot page reload).
      }
    );

  return Preloadable;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const preloadRoute = <T extends ComponentType<any> = ComponentType<any>>(importFn: () => Promise<{ default: T }>) => {
  importFn();
};

export const lazyImports = {
  auth: {
    CompanyAuth: lazyWithRetry(() => import("@/pages/company-auth")),
    AuthLogin: lazyWithRetry(() => import("@/pages/auth-login")),
    AuthSignup: lazyWithRetry(() => import("@/pages/auth-signup")),
    AuthRecover: lazyWithRetry(() => import("@/pages/auth-recover")),
    AuthConfirm: lazyWithRetry(() => import("@/pages/auth-confirm")),
  },

  dashboard: {
    CandidateDashboard: lazyWithRetry(() => import("@/pages/candidate-dashboard")),
    CompanyDashboard: lazyWithRetry(() => import("@/pages/company-dashboard")),
  },

  jobManagement: {
    JobPosting: lazyWithRetry(() => import("@/pages/job-posting")),
    JobsOverview: lazyWithRetry(() => import("@/pages/jobs-overview")),
    JobEdit: lazyWithRetry(() => import("@/pages/job-edit")),
    JobView: lazyWithRetry(() => import("@/pages/job-view")),
  },

  jobSearch: {
    JobSearch: lazyWithRetry(() => import("@/pages/job-search")),
    JobDetail: lazyWithRetry(() => import("@/pages/job-detail")),
  },

  aiMatching: {
    OptimizedJobRecommendations: lazyWithRetry(() => import("@/components/OptimizedJobRecommendations")),
    MatchingAnalysis: lazyWithRetry(() => import("@/components/MatchingAnalysis")),
    CandidateMatching: lazyWithRetry(() => import("@/components/CandidateMatching")),
    AIMatchingTest: lazyWithRetry(() => import("@/pages/ai-matching-test")),
  },

  profile: {
    CandidateProfile: lazyWithRetry(() => import("@/pages/candidate-profile")),
    CompanyProfile: lazyWithRetry(() => import("@/pages/company-profile")),
    MockInterview: lazyWithRetry(() => import("@/pages/mock-interview")),
  },

  setup: {
    CompanySetup: lazyWithRetry(() => import("@/pages/company-setup")),
  },
};

export const preloadCriticalRoutes = (userRole: 'candidate' | 'company') => {
};

export const preloadOnHover = {
  jobSearch: () => preloadRoute(() => import("@/pages/job-search")),
  jobPosting: () => preloadRoute(() => import("@/pages/job-posting")),
  profile: () => preloadRoute(() => import("@/pages/candidate-profile")),
  mockInterview: () => preloadRoute(() => import("@/pages/mock-interview")),
  aiMatching: () => preloadRoute(() => import("@/components/OptimizedJobRecommendations")),
};
